import { useQuery } from '@tanstack/react-query';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import loginBackground from '@/assets/login-background.webp';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { useSignIn } from '@/lib/auth';

type AuthConfig = { googleClientId: string; testMode: boolean };

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize(opts: {
            client_id: string;
            callback: (r: { credential: string }) => void;
            ux_mode?: string;
          }): void;
          renderButton(el: HTMLElement, opts: Record<string, unknown>): void;
        };
      };
    };
  }
}

const GSI_SRC = 'https://accounts.google.com/gsi/client';

function loadGsi(): Promise<void> {
  if (window.google?.accounts) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GSI_SRC}"]`);
    const s = existing ?? Object.assign(document.createElement('script'), { src: GSI_SRC, async: true });
    s.addEventListener('load', () => resolve());
    s.addEventListener('error', () => reject(new Error('Could not load Google sign-in')));
    if (!existing) document.head.appendChild(s);
  });
}

export default function LoginPage() {
  const config = useQuery({
    queryKey: ['auth-config'],
    queryFn: () => api.get<AuthConfig>('/api/auth/config'),
  });
  const signIn = useSignIn();
  const buttonRef = useRef<HTMLDivElement>(null);
  const [devEmail, setDevEmail] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);

  const clientId = config.data?.googleClientId;
  const { mutate } = signIn;
  useEffect(() => {
    if (!clientId || import.meta.env.MODE === 'test') return;
    let cancelled = false;
    loadGsi()
      .then(() => {
        if (cancelled || !buttonRef.current || !window.google) return;
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: ({ credential }) => mutate({ credential }),
        });
        window.google.accounts.id.renderButton(buttonRef.current, {
          theme: 'filled_blue',
          size: 'large',
          text: 'continue_with',
          shape: 'rectangular',
          width: 300,
        });
      })
      .catch((e: Error) => setLoadError(e.message));
    return () => {
      cancelled = true;
    };
  }, [clientId, mutate]);

  const onDevSubmit = (e: FormEvent) => {
    e.preventDefault();
    signIn.mutate({ email: devEmail.trim() });
  };

  const error = signIn.error?.message ?? loadError;

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-background bg-cover bg-center p-4"
      style={{ backgroundImage: `url(${loginBackground})` }}
    >
      <div className="w-full max-w-sm rounded-md border bg-card/80 p-8 shadow-sm">
        <div className="mb-6 flex items-center gap-2">
          <img src="/favicon.svg" alt="" className="size-8" />
          <span className="text-2xl font-medium text-foreground">Taim</span>
        </div>
        <h1 className="mb-1 text-lg font-medium">Sign in to Taim</h1>
        <p className="mb-6 text-muted-foreground">Use your company Google Workspace account.</p>

        <div ref={buttonRef} data-testid="google-signin" className="flex min-h-11 justify-center" />

        {error && (
          <p role="alert" className="mt-4 rounded-sm bg-destructive/10 p-2 text-destructive">
            {error}
          </p>
        )}

        {config.data?.testMode && (
          <form onSubmit={onDevSubmit} className="mt-6 space-y-2 border-t pt-4">
            <Label htmlFor="dev-email">Developer email</Label>
            <Input
              id="dev-email"
              type="email"
              value={devEmail}
              onChange={(e) => setDevEmail(e.target.value)}
              placeholder="you@futluz.com"
              required
            />
            <Button type="submit" variant="outline" className="w-full" disabled={signIn.isPending}>
              Developer sign in
            </Button>
          </form>
        )}

        <footer className="mt-6 border-t pt-4 text-center text-sm text-muted-foreground">
          © 2026, Futluz Tech Inc.
        </footer>
      </div>
    </div>
  );
}
